/**
 * Emit auto-links describing the build-time relationships inside a SINK
 * INSTALLATION — a basin in a counter over a base cabinet, with a faucet
 * behind it — the twin of `vanity-assembly/emitVanityLinks`.
 *
 * Named for the kitchen because that is where it started, but the shape is
 * what it matches, not the vocabulary: a cabinet-run's VANITY profile builds
 * the same installation out of `sink` + `bathroom-sink-faucet`, and
 * `materializeRun` routes every manual run's sink group here whatever its
 * profile. `isSinkFaucetLike` is what makes both read (see its docstring for
 * why the vanity-assembly emitter is the wrong tool for a RUN group).
 *
 * Pure — no I/O, no mutation. Returns `groupCustomization.links` entries with
 * `source: 'auto'` so the group editor can tell them from user-drawn links.
 *
 * The blueprint is deliberately small, because a kitchen sink group has
 * exactly one relationship the geometry actually depends on:
 *
 *   sink → faucet    rp      (setback behind the basin + shared facing)
 *   two basins       copy    (double-bowl / two-basin island)
 *   the accessories over it    `emitAccessoryLinks` (a VANITY installation
 *                              only — a kitchen sink has no mirror)
 *
 * The countertop is NOT linked. It is a host SURFACE, not a positioned
 * sibling: it is generated to fit the cabinet run, so an rp constraint would
 * fight the run geometry every time the run is re-materialized. Its
 * relationship to the sink and faucet is anchoring — "my deck Y comes from
 * you" — which belongs in `parents[]`, where the builders record it.
 *
 * The last two rules are why this module, and not
 * `vanity-assembly/emitVanityLinks`, records a vanity RUN's group. A run's
 * accessories are group-owned and the run itself never re-emits them
 * (`cabinet-runs/vanityRunAccessories.js`), but their relationship to the
 * basin still has to be written down, and ONE group may only have ONE
 * emitter: two would each bake their own constraint set over the same pairs,
 * and a pair holding both an `rp` and a `rail-x` disagrees by more than the
 * solver's half-millimetre epsilon, so `solveDrag` rejects and the vanity
 * cannot be dragged at all (the trap `emitVanityLinks` rule 7 avoids WITHIN
 * itself). A mirror over a kitchen sink does not happen, so these rules are
 * inert on the path this file is named for.
 *
 * Anchor = the base cabinet, else the basin (see `getKitchenSinkAnchor`),
 * matching the editor's `resolveEditAnchor` preference so the links are
 * reachable from whichever member the user clicks.
 */

const {
  computeRpOffset,
  computeRpRotYDiff,
  createLink,
  buildAnchorLocal,
  nearestByDx,
  isWallMounted,
  mountedWallId,
} = require('../group-customization');
const {
  isSinkLike,
  isSinkFaucetLike,
  getKitchenSinkAnchor,
} = require('./fixtureGroup');
const { getFixtureGroupMirrors } = require('../vanity-assembly/fixtureGroup');
const { emitAccessoryLinks } = require('../vanity-assembly/emitAccessoryLinks');

/** An rp link with `source: 'auto'`, offset in the anchor-local frame. */
function makeRpLink(sourceLocal, targetLocal) {
  if (!sourceLocal || !targetLocal) return null;
  return createLink('rp', {
    source: 'auto',
    sourceId: sourceLocal.id,
    targetId: targetLocal.id,
    offset: computeRpOffset(sourceLocal, targetLocal, 'copy'),
    rotYDiff: computeRpRotYDiff(sourceLocal, targetLocal),
    rotationMode: 'copy',
  });
}

/**
 * @param {object[]} groupObjects — world-space objects of ONE fixture group.
 * @returns {object[]} links for `groupCustomization.links`; empty when the
 *   group has no anchor, no basin, or a basin with nothing over it — neither
 *   a faucet nor an accessory.
 */
function emitKitchenSinkLinks(groupObjects) {
  if (!Array.isArray(groupObjects) || groupObjects.length === 0) return [];

  const anchor = getKitchenSinkAnchor(groupObjects);
  if (!anchor) return [];

  const sinks = groupObjects.filter(isSinkLike);
  const faucets = groupObjects.filter(isSinkFaucetLike);
  // A group id is shared by every member, so the accessory lookups can read
  // it off the anchor; passing `null` would make them drop everything.
  const groupId = anchor.fixtureGroupId ?? null;
  const mirrors = getFixtureGroupMirrors(groupObjects, groupId);
  // Nothing to relate: no basin at all, or a basin with neither a faucet nor
  // an accessory over it.
  if (sinks.length === 0) return [];
  if (faucets.length === 0 && mirrors.length === 0) return [];

  const localById = new Map();
  for (const obj of [anchor, ...sinks, ...faucets]) {
    if (!obj?.id || localById.has(obj.id)) continue;
    const local = buildAnchorLocal(obj, anchor);
    if (local) localById.set(obj.id, local);
  }

  const links = [];

  // 1) Each faucet is driven by its basin. A faucet that names its sink in
  //    `parents` uses that; otherwise pair on anchor-local dx, which is what
  //    separates the two halves of a double-bowl run.
  const sinkLocals = sinks.map((s) => localById.get(s.id)).filter(Boolean);
  for (const faucet of faucets) {
    const faucetLocal = localById.get(faucet.id);
    if (!faucetLocal) continue;
    const namedSinkId = (faucet.parents || []).find((p) =>
      /sink/i.test(p?.type || '')
    )?.id;
    const sourceLocal =
      (namedSinkId && localById.get(namedSinkId)) ||
      nearestByDx(faucetLocal, sinkLocals);
    if (!sourceLocal) continue;
    const link = makeRpLink(sourceLocal, faucetLocal);
    if (link) links.push(link);
  }

  // 2) Double-bowl: keep the two basins' own children in step, matching the
  //    double-vanity sink rule.
  if (sinkLocals.length === 2) {
    const [a, b] = [...sinkLocals].sort((x, y) => x.dx - y.dx);
    const link = createLink('copy', {
      source: 'auto',
      parentAId: a.id,
      parentBId: b.id,
    });
    if (link) links.push(link);
  }

  // 2b) The accessories over the installation — the mirror, its lights, the
  //     sconces beside it — are `emitAccessoryLinks`, the SAME rules the
  //     vanity-assembly emitter uses. Shared rather than reimplemented
  //     because `mergeLinks` regenerates auto-links wholesale: a vanity that
  //     is built by `build_vanity` and then given a run is recorded by
  //     whichever emitter ran last, so an emitter that knew fewer rules would
  //     silently drop the group's sconce pair on the first bay edit.
  //
  //     A mirror hangs over the FAUCET where there is one, else the basin.
  //     Inert on the path this file is named for: a kitchen sink has no
  //     mirror over it.
  links.push(
    ...emitAccessoryLinks({
      groupObjects,
      anchor,
      mirrorSources: faucets.length ? faucets : sinks,
    }).links
  );

  // 3) Wall mounting, stated: a wall-mount faucet (rare in kitchens, real
  //    for pot fillers) gets an on-wall link to the wall it hangs on.
  for (const o of groupObjects) {
    if (!o?.id || !isWallMounted(o)) continue;
    const wallId = mountedWallId(o);
    if (!wallId) continue;
    const link = createLink('on-wall', {
      source: 'auto',
      subjectId: o.id,
      wall: { kind: 'wall', id: wallId },
    });
    if (link) links.push(link);
  }

  return links.filter(Boolean);
}

module.exports = { emitKitchenSinkLinks };
